/**
 * Back-transformation to the proportion scale (brief 9.10, MET-09).
 *
 * Arcsine scale (the default, brief 9.5): the EBLUP thetatilde_i is
 * clamped to [0, pi/2], then:
 *   bias-corrected:  ptilde_i = 0.5 * (1 - cos(2 thetatilde_i) exp(-2 s2_i))
 *   naive:           ptilde_i = sin(thetatilde_i)^2
 * with s2_i = g1_i + g2_i, the conditional variance of theta_i. The
 * bias-corrected form is the exact expectation of sin(theta)^2 for a
 * normal theta with mean thetatilde_i and variance s2_i.
 *
 * Intervals are built on the transformed scale and mapped through
 * sin^2, so they stay inside [0, 1] by construction:
 *   theta_L/U = clamp(thetatilde_i -/+ z sqrt(mse_theta_i))
 *   pL_i = sin(theta_L)^2,  pU_i = sin(theta_U)^2
 *   rmse_p_i = (pU_i - pL_i) / (2 z),  cv_i = rmse_p_i / ptilde_i.
 *
 * Linear scale (Expert, brief 9.5): the fitted scale IS the proportion
 * scale — thetahat_d = phat_d — so the back-transformation of 9.10,
 * whose every formula is stated for the arcsine map (the [0, pi/2]
 * clamp, the sin^2 interval map, the exact expectation of sin^2), is
 * the identity: ptilde_i = clamp(thetatilde_i, 0, 1), and the interval
 * is the theta-scale interval clamped to [0, 1]. The reported
 * standard error on this scale is sqrt(mse_theta_i) (directive C-3,
 * review item R17): the clamped-interval proxy (pU_i - pL_i) / (2 z)
 * understates uncertainty whenever an interval end clamps at 0 or 1.
 * In Full mode the analysis layer replaces it with sqrt(mse_p_i)
 * from the bootstrap, the same quantity on this scale. The bc and
 * naive settings coincide on this scale: there is no nonlinear map
 * whose expectation a correction could adjust. See
 * validation/parity/OPEN_QUESTIONS.md Q44 (resolved in Block 9).
 */
import { normalQuantile } from './distributions.js';
const HALF_PI = Math.PI / 2;
function clampTheta(t) {
    return Math.min(Math.max(t, 0), HALF_PI);
}
/**
 * Workstream R (combined gate R-6, Decision Log Addendum 89): the
 * exact moments of sin^2(X) and sin^4(X) for X ~ N(theta, s2) on
 * the arcsine scale, in closed form. From sin^2(x) = (1 - cos 2x)/2
 * and the normal characteristic function E[cos(kX)] =
 * cos(k*theta) * exp(-k^2 s2 / 2):
 *   E[sin^2 X] = 1/2 * (1 - cos(2 theta) * exp(-2 s2));
 *   E[sin^4 X] = 3/8 - 1/2 * cos(2 theta) * exp(-2 s2)
 *              + 1/8 * cos(4 theta) * exp(-8 s2).
 */
export function arcsinePredictiveMoments(theta, s2) {
    const cos2 = Math.cos(2 * theta);
    const e2 = Math.exp(-2 * s2);
    return {
        eSin2: 0.5 * (1 - cos2 * e2),
        eSin4: 0.375 - 0.5 * cos2 * e2 + 0.125 * Math.cos(4 * theta) * Math.exp(-8 * s2),
    };
}
/**
 * Workstream R: the proportion-scale MSE of a published estimate
 * pHat under the arcsine-scale predictive distribution centered at
 * theta (the benchmarked center the published interval uses) with
 * variance s2 (the shipped arcsine-scale MSE):
 *   MSE_p = E[sin^4 X] - 2 pHat E[sin^2 X] + pHat^2.
 * Floored at 0: the expression is a variance and a negative value
 * can arise only by rounding. Unlike the interval-width standard
 * error it replaces under the study switch, MSE_p does not vanish
 * as pHat approaches 0.
 */
export function proportionScaleMseR(theta, s2, pHat) {
    const { eSin2, eSin4 } = arcsinePredictiveMoments(theta, s2);
    const mse = eSin4 - 2 * pHat * eSin2 + pHat * pHat;
    return mse > 0 ? mse : 0;
}
function clampProportion(p) {
    return Math.min(Math.max(p, 0), 1);
}
export function backTransformAreasImpl(fit, options) {
    const z = normalQuantile(0.5 + options.confidence / 2);
    const linear = options.transform === 'linear';
    return fit.areas.map((area) => {
        const halfWidth = z * Math.sqrt(Math.max(0, area.mseTheta));
        let estimate;
        let lower;
        let upper;
        let rmse;
        if (linear) {
            estimate = clampProportion(area.eblupTheta);
            lower = clampProportion(estimate - halfWidth);
            upper = clampProportion(estimate + halfWidth);
            // Directive C-3: on the linear scale the standard error is
            // sqrt(mse_theta) (Quick) — the fitted scale is the proportion
            // scale, so mse_theta is already a proportion-scale variance.
            rmse = Math.sqrt(Math.max(0, area.mseTheta));
        }
        else {
            const theta = clampTheta(area.eblupTheta);
            const s2 = area.g1 + area.g2;
            estimate =
                options.method === 'bc'
                    ? 0.5 * (1 - Math.cos(2 * theta) * Math.exp(-2 * s2))
                    : Math.sin(theta) * Math.sin(theta);
            lower = Math.sin(clampTheta(theta - halfWidth)) ** 2;
            upper = Math.sin(clampTheta(theta + halfWidth)) ** 2;
            rmse =
                options.reportedPrecisionR === true
                    ? Math.sqrt(proportionScaleMseR(theta, area.mseTheta, estimate))
                    : (upper - lower) / (2 * z);
        }
        const cv = estimate > 0 ? rmse / estimate : rmse > 0 ? Number.POSITIVE_INFINITY : 0;
        return {
            areaId: area.areaId,
            estimate,
            lower,
            upper,
            rmse,
            cv,
            estimateOutsideInterval: estimate < lower || estimate > upper,
            mseTheta: area.mseTheta,
            eblupTheta: area.eblupTheta,
        };
    });
}
/**
 * Assemble a cell's reported estimates from a parametric bootstrap
 * (brief 9.12): each estimate keeps the back-transformed point
 * estimate and takes its interval from the bootstrap MSEs — built
 * on the fitted scale around the EBLUP and mapped back (sin^2 on the
 * arcsine scale; the identity clamped to [0, 1] on the linear
 * scale), with the reported standard error sqrt(mse_p) on the
 * proportion scale and mse_theta carried for benchmarking's
 * interval recomputation (brief 9.13).
 *
 * This is THE bootstrap-interval construction of both pipelines:
 * runAnalysis (analysis.ts) and the web area-level pipeline
 * (apps/web/src/pipeline/areaLevel.ts) both call it, so a bootstrap
 * interval is formed by exactly one implementation (Tier 2 Item 7
 * deduplication; the web pipeline previously carried a private
 * copy whose linear branch formed the half-width from mse_p
 * instead of mse_theta — see docs/TIER2_ITEM7_AUDIT.md).
 * Estimates whose area is missing from the fit or the bootstrap
 * result pass through unchanged.
 */
export function assembleBootstrapEstimatesImpl(estimates, fit, boot, settings) {
    const z = normalQuantile(0.5 + settings.confidence / 2);
    const mseByArea = new Map(boot.areas.map((a) => [a.areaId, a]));
    return estimates.map((e) => {
        const areaFit = fit.areas.find((a) => a.areaId === e.areaId);
        const b = mseByArea.get(e.areaId);
        if (areaFit === undefined || b === undefined)
            return e;
        const half = z * Math.sqrt(Math.max(0, b.mseTheta));
        let lower;
        let upper;
        if (settings.transform === 'linear') {
            const center = Math.min(Math.max(areaFit.eblupTheta, 0), 1);
            lower = Math.min(Math.max(center - half, 0), 1);
            upper = Math.min(Math.max(center + half, 0), 1);
        }
        else {
            const center = Math.min(Math.max(areaFit.eblupTheta, 0), HALF_PI);
            lower = Math.sin(Math.min(Math.max(center - half, 0), HALF_PI)) ** 2;
            upper = Math.sin(Math.min(Math.max(center + half, 0), HALF_PI)) ** 2;
        }
        const rmse = settings.reportedPrecisionR === true && settings.transform !== 'linear'
            ? Math.sqrt(proportionScaleMseR(Math.min(Math.max(areaFit.eblupTheta, 0), HALF_PI), b.mseTheta, e.estimate))
            : Math.sqrt(Math.max(0, b.mseP));
        const cv = e.estimate > 0 ? rmse / e.estimate : rmse > 0 ? Number.POSITIVE_INFINITY : 0;
        return {
            ...e,
            lower,
            upper,
            rmse,
            cv,
            estimateOutsideInterval: e.estimate < lower || e.estimate > upper,
            mseTheta: b.mseTheta,
        };
    });
}
//# sourceMappingURL=backtransform.js.map